import React from 'react'

import { Button, Modal, ModalInjectedProps } from '@sketch/components'
import {
  ProjectFragment,
  ShareListItemFragment,
  useArchiveStorageItemMutation,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import {
  removeFromPaginated,
  removeFromStorageItemsList,
} from '@sketch/modules-common'

interface ArchiveStorageItemModalProps extends ModalInjectedProps {
  item:
    | Pick<
        ShareListItemFragment,
        'identifier' | '__typename' | 'name' | 'project'
      >
    | Pick<
        ProjectFragment,
        'identifier' | '__typename' | 'name' | 'parentProjectIdentifier'
      >
}

export const ArchiveStorageItemModal = (
  props: ArchiveStorageItemModalProps
) => {
  const { hideModal, item } = props
  const { showToast } = useToast()

  const [archiveItem, { loading }] = useArchiveStorageItemMutation({
    variables: {
      identifier: item.identifier,
      type: item.__typename === 'Project' ? 'PROJECT' : 'SHARE',
    },
    onError: 'show-toast',
    onCompleted: ({ archiveStorageItem }) => {
      showToast(`“${archiveStorageItem.storageItem.name}” Archived`)
      hideModal()
    },
    update: cache => {
      removeFromStorageItemsList({ cache, target: item })

      const listToRemoveFrom = ['.projects', '.shares']
      removeFromPaginated(cache, item, key =>
        listToRemoveFrom.some(k => key.includes(k))
      )
    },
  })

  const typeTerm = item.__typename === 'Project' ? 'Folder' : 'Document'
  const typeLower = typeTerm.toLowerCase()

  return (
    <Modal onCancel={hideModal}>
      <Modal.Header>Archive {typeTerm}?</Modal.Header>
      <Modal.Body>
        The <strong>“{item.name}”</strong> {typeLower}
        {item.__typename === 'Project' ? ' and all its documents' : ''}
        will be sent to the Archive. You’ll still be able to view and edit
        archived documents as usual.
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => archiveItem()}
          loading={loading}
        >
          Archive {typeTerm}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
